import React, { useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField } from '@mui/material';
import { SearchButton } from '../SearchButton';
import { ClearButton } from '../ClearButton';
import { CurrentLocationButton } from '../CurrentLocationButton';
import { useStateContext, useGoogleMapsContext } from '../../context';
import { pushToDataLayer, sortStoresByDistance } from '../../utils';
import { breakpoints, colors } from '../../constants';
import { IAutoCompleteOption } from '../../types';

const Title = styled.h2``;
const SubText = styled.p``;
const ButtonWrapper = styled.div``;

const BaseLocationBar = ({
    className,
}: React.HTMLAttributes<HTMLDivElement>) => {
    const { t } = useTranslation();
    const [autoCompleteOptions, setAutoCompleteOptions] = useState<
        IAutoCompleteOption[]
    >([]);
    const {
        availableStores,
        findingCurrentLocation,
        searchInputValue,
        searchLocation,
        centerLocation,
        useCurrentLocation,
        isOakley,
        isNuance,
        setCenterLocation,
        setPlaceId,
        setSearchInputValue,
        setSearchLocation,
        setUseCurrentLocation,
    } = useStateContext();
    const sessionToken = useRef(
        new google.maps.places.AutocompleteSessionToken()
    );
    const { googleMapsRef } = useGoogleMapsContext();

    const handleCurrentLocationButtonClick = () => {
        setUseCurrentLocation(true);

        pushToDataLayer('storeSearchSubmit', {
            action: {
                step: 'store locator search',
                interaction: 'current location',
                location: 'current location',
            },
        });
    };

    const handleLocationSelect = (placeId: string, searchTerm: string) => {
        setPlaceId(placeId);
        pushToDataLayer('storeSearchSubmit', {
            action: {
                step: 'store locator search',
                interaction: 'location typed',
                location: searchTerm,
                searchResults: availableStores?.length,
            },
        });

        // Set a new session token after a place is selected
        sessionToken.current =
            new google.maps.places.AutocompleteSessionToken();
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                const request = {
                    componentRestrictions: {
                        country: 'be',
                    },
                    fields: ['name', 'placeId'],
                    input: value,
                    sessionToken: sessionToken.current,
                    types: ['(regions)'],
                };

                googleMapsRef.current.autoCompleteService
                    .getPlacePredictions(request)
                    .then(
                        (response: google.maps.places.AutocompleteResponse) => {
                            setAutoCompleteOptions(
                                response.predictions.map((prediction) => ({
                                    label: prediction.description,
                                    placeId: prediction.place_id,
                                }))
                            );
                        }
                    );
            }, 300),
        [sessionToken, googleMapsRef]
    );

    const handleSearchInputChange = (value: string) => {
        debouncedSearch(value);
    };

    const inputValue = useCurrentLocation
        ? t('currentLocation')
        : searchInputValue;

    const handleClearButtonClick = () => {
        const referenceLocation = searchLocation || centerLocation;

        if (availableStores && referenceLocation) {
            const storesSortedByDistance = sortStoresByDistance(
                availableStores,
                referenceLocation
            );

            const { lat, lng } = storesSortedByDistance[0];
            const storePosition = {
                lat,
                lng,
            };

            setCenterLocation(storePosition);
        }

        setUseCurrentLocation(false);
        setSearchInputValue('');
        setSearchLocation(undefined);
    };

    useEffect(() => {
        if (searchInputValue === '') {
            setAutoCompleteOptions([]);

            return;
        }

        handleSearchInputChange(searchInputValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInputValue]);

    const muiInputStyles = {
        '& .MuiOutlinedInput-root': {
            ' border-radius': '6px',

            padding: '6px 12px',
            'font-family': 'inherit',
            'font-size': 'inherit',
            'line-height': 'inherit',
            'font-weight': `${useCurrentLocation ? '500' : '400'}`,
            color: `${useCurrentLocation ? colors.orange : colors.black}`,

            '.MuiOutlinedInput-notchedOutline': {
                borderColor: colors.greyInputBorder,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.greyInputBorder,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.greyInputBorder,
            },
            '&.Mui-focused': {
                outline: `4px solid ${colors.greyInputBorder}`,
                outlineOffset: '2px',
                borderRadius: '6px',
            },
        },
    };

    const title = isOakley ? t('oakley.title') : isNuance ? t('nuance.title') : t('rayban.title');
    const subtext = isOakley ? t('oakley.subtext') : isNuance ? t('nuance.subtext') : t('rayban.subtext');

    return (
        <div className={className}>
            <Title>{title}</Title>
            <SubText>{subtext}</SubText>
            <SubText>{t('searchBelow')}</SubText>
            <Autocomplete
                autoHighlight
                autoSelect
                disableClearable
                freeSolo
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.label
                }
                inputValue={inputValue}
                options={autoCompleteOptions}
                renderInput={(params) => (
                    <>
                        <TextField
                            {...params}
                            fullWidth
                            InputProps={{
                                ...params.InputProps,
                            }}
                            placeholder={t('searchPlaceholder')}
                            variant="outlined"
                            sx={muiInputStyles}
                        />
                        <ButtonWrapper>
                            {inputValue !== '' && (
                                <ClearButton onClick={handleClearButtonClick} />
                            )}
                            <SearchButton />
                        </ButtonWrapper>
                    </>
                )}
                onChange={(_e, value) => {
                    if (value && typeof value !== 'string') {
                        handleLocationSelect(value.placeId, value.label);
                    }
                }}
                onInputChange={(_e, val) => setSearchInputValue(val)}
            />
            {!useCurrentLocation && (
                <CurrentLocationButton
                    loading={findingCurrentLocation}
                    onClick={handleCurrentLocationButtonClick}
                />
            )}
        </div>
    );
};

export const LocationBar = styled(BaseLocationBar)`
    padding-bottom: 20px;

    .MuiAutocomplete-root {
        position: relative;
        margin-top: 1.5rem;
    }

    @media (${breakpoints.mediumMin}) {
        display: block;
    }

    ${SubText} {
        line-height: 1.6;
    }

    ${SubText}:last-of-type {
        margin-top: 10px;
    }

    ${CurrentLocationButton} {
        height: 48px;
        margin-top: 1rem;
    }

    ${ButtonWrapper} {
        align-items: center;
        bottom: 0;
        display: flex;
        padding-right: 0.75rem;
        position: absolute;
        right: 0;
        top: 0;
    }

    ${SearchButton} {
        height: 1.375rem;
        width: 1.375rem;
        margin-left: 0.75rem;
    }

    ${Title} {
        font-size: 24px;
        margin-bottom: 20px;
        color: ${colors.offBlack};
        line-height: 1.4;
        font-weight: 700;

        @media (${breakpoints.mediumMin}) {
            text-align: center;
            text-align: left;
        }
    }
`;
